<template>
  <div class="contract-document-actions">
    <v-menu v-if="hasDocument || canManage" offset-y left :disabled="busy">
      <template v-slot:activator="{ on: menu, attrs }">
        <v-tooltip top>
          <template v-slot:activator="{ on: tooltip }">
            <v-btn
              icon
              small
              class="document-button"
              v-bind="attrs"
              :color="hasDocument ? 'primary' : undefined"
              :loading="busy"
              :disabled="busy"
              :aria-label="tooltipText"
              v-on="{ ...tooltip, ...menu }"
            >
              <v-icon small>{{ hasDocument ? 'mdi-file-pdf-box' : 'mdi-file-upload-outline' }}</v-icon>
            </v-btn>
          </template>
          <span class="tooltip-text">{{ tooltipText }}</span>
        </v-tooltip>
      </template>

      <v-list dense>
        <template v-if="hasDocument">
          <v-list-item class="action-view" @click="view">
            <v-list-item-icon><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Ver PDF</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="canManage" class="action-replace" @click="pickFile('replace')">
            <v-list-item-icon><v-icon small>mdi-file-replace-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Reemplazar PDF</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="canManage" class="action-delete" @click="deleteDialog = true">
            <v-list-item-icon><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
            <v-list-item-title class="error--text">Eliminar PDF</v-list-item-title>
          </v-list-item>
        </template>
        <v-list-item v-else-if="canManage" class="action-upload" @click="pickFile('upload')">
          <v-list-item-icon><v-icon small>mdi-upload</v-icon></v-list-item-icon>
          <v-list-item-title>Cargar PDF</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <input
      ref="fileInput"
      type="file"
      accept="application/pdf,.pdf"
      class="file-input"
      @change="onFileChosen"
    />

    <!-- Confirmar reemplazo: se cierra con Escape o clic afuera, sin cambios. -->
    <v-dialog v-model="replaceDialog" max-width="480" @keydown.esc="replaceDialog = false">
      <v-card class="pa-2">
        <v-card-title class="text-uppercase">Reemplazar PDF</v-card-title>
        <hr class="my-2" />
        <v-card-text>
          <div class="pw-label">PDF actual</div>
          <div class="pw-value text-truncate mb-3 current-name">{{ currentFileName }}</div>
          <div class="pw-label">PDF nuevo</div>
          <div class="pw-value text-truncate mb-3 new-name">{{ pendingFile ? pendingFile.name : '' }}</div>
          <div class="text-body-2">El PDF anterior queda guardado como historial; en la tabla solo se verá el nuevo.</div>
        </v-card-text>
        <hr class="my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded class="cancel-replace" @click="replaceDialog = false">Cancelar</v-btn>
          <v-btn color="primary" rounded class="confirm-replace" @click="confirmReplace">Reemplazar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmar eliminación: mismo formato que eliminar contrato. -->
    <v-dialog v-model="deleteDialog" max-width="480" @keydown.esc="deleteDialog = false">
      <v-card class="pa-2">
        <v-card-title class="text-uppercase">Eliminar PDF del contrato {{ contrato.codigo || '' }}</v-card-title>
        <hr class="my-2" />
        <v-card-text>
          <div class="pw-value text-truncate mb-2 delete-name">{{ currentFileName }}</div>
          ¿Está seguro de que desea eliminar el PDF de este contrato?
        </v-card-text>
        <hr class="my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded class="cancel-delete" @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" rounded class="confirm-delete" @click="confirmDelete">Sí, eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ContratoDocumentService from '../services/contrato-document.service';
import { can } from '../utils/permissions';

/** Largest PDF the backend accepts (20 MB). */
export const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

/** User-facing messages by backend error code (never the raw backend message). */
export const DOCUMENT_ERROR_MESSAGES = {
  CONTRATO_DOCUMENT_ALREADY_EXISTS: 'Este contrato ya tiene un PDF. Recarga la página para verlo.',
  CONTRATO_DOCUMENT_NOT_FOUND: 'El contrato ya no tiene PDF. Recarga la página.',
  INVALID_DOCUMENT: 'Solo se aceptan archivos PDF.',
  DOCUMENT_TOO_LARGE: 'El PDF supera el máximo de 20 MB.'
};
const GENERIC_ERROR = 'No se pudo completar la acción con el PDF. Intenta de nuevo.';

/** `dd-mm-yyyy` in local time. */
function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
}

/** Client-side check (UX only; the backend validates for real). Returns an error code or null. */
function validateFile(file) {
  const looksPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
  if (!looksPdf || !file.size) return 'INVALID_DOCUMENT';
  if (file.size > MAX_DOCUMENT_BYTES) return 'DOCUMENT_TOO_LARGE';
  return null;
}

/**
 * PDF button of a contract row: indicator (with / without PDF) and menu to
 * view, upload, replace or delete it (pawer-admin specs/029-b2b-contract-documents).
 *
 * Events:
 * - `updated` → `{ contratoId, document }`, with `document` = `{ id, originalFileName, uploadedAt }` or `null`.
 * - `notify` → `{ type: 'success' | 'error', message }`, for the view's snackbar.
 */
export default {
  name: 'ContractDocumentActions',

  props: {
    contrato: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      busy: false,
      mode: 'upload',
      pendingFile: null,
      replaceDialog: false,
      deleteDialog: false
    };
  },

  computed: {
    document() {
      return this.contrato.document || null;
    },
    hasDocument() {
      return Boolean(this.document);
    },
    canManage() {
      return can('contracts:manage-documents');
    },
    currentFileName() {
      return this.document ? this.document.originalFileName : '';
    },
    tooltipText() {
      if (!this.document) return 'Sin PDF';
      return `PDF: ${this.document.originalFileName} · cargado el ${formatDate(this.document.uploadedAt)}`;
    }
  },

  watch: {
    replaceDialog(open) {
      // Escape, clic afuera o "Cancelar": el archivo elegido se descarta.
      if (!open) this.pendingFile = null;
    }
  },

  methods: {
    pickFile(mode) {
      this.mode = mode;
      this.$refs.fileInput.click();
    },

    onFileChosen(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      // Limpia el input para que elegir el mismo archivo otra vez vuelva a disparar `change`.
      input.value = '';
      if (!file) return;

      const invalid = validateFile(file);
      if (invalid) {
        this.notifyError(invalid);
        return;
      }
      if (this.mode === 'replace') {
        this.pendingFile = file;
        this.replaceDialog = true;
        return;
      }
      this.upload(file);
    },

    async upload(file) {
      const res = await this.run(() => ContratoDocumentService.upload(this.contrato.id, file));
      if (res.status !== 'ok') return;
      this.emitDocument(res.data);
      this.notify('success', 'PDF cargado');
    },

    async confirmReplace() {
      const file = this.pendingFile;
      this.replaceDialog = false;
      if (!file) return;
      const res = await this.run(() => ContratoDocumentService.replace(this.contrato.id, file));
      if (res.status !== 'ok') return;
      this.emitDocument(res.data);
      this.notify('success', 'PDF reemplazado');
    },

    async confirmDelete() {
      this.deleteDialog = false;
      const res = await this.run(() => ContratoDocumentService.remove(this.contrato.id));
      if (res.status !== 'ok') return;
      this.emitDocument(null);
      this.notify('success', 'PDF eliminado');
    },

    /**
     * Opens the tab synchronously, inside the click, so the browser does not
     * block it as a popup; the signed URL only exists after the request.
     */
    async view() {
      const tab = window.open('', '_blank');
      const res = await this.run(() => ContratoDocumentService.getViewUrl(this.contrato.id));
      if (res.status !== 'ok') {
        if (tab) tab.close();
        return;
      }
      if (!tab) {
        this.notify('error', 'El navegador bloqueó la pestaña nueva. Permite ventanas emergentes e intenta de nuevo.');
        return;
      }
      tab.opener = null;
      tab.location.href = res.data.url;
    },

    /** Runs one PDF action with the row busy; maps errors to a message. */
    async run(action) {
      this.busy = true;
      try {
        const res = await action();
        if (res.status !== 'ok') this.handleError(res.code);
        return res;
      } finally {
        this.busy = false;
      }
    },

    handleError(code) {
      // El PDF ya no existe (otro admin lo eliminó): la fila deja de mostrarlo.
      if (code === 'CONTRATO_DOCUMENT_NOT_FOUND') this.emitDocument(null);
      this.notifyError(code);
    },

    emitDocument(data) {
      const document = data
        ? { id: data.id, originalFileName: data.originalFileName, uploadedAt: data.uploadedAt }
        : null;
      this.$emit('updated', { contratoId: this.contrato.id, document });
    },

    notifyError(code) {
      this.notify('error', DOCUMENT_ERROR_MESSAGES[code] || GENERIC_ERROR);
    },

    notify(type, message) {
      this.$emit('notify', { type, message });
    }
  }
};
</script>

<style scoped>
.contract-document-actions {
  display: inline-block;
}

.file-input {
  display: none;
}

.tooltip-text {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
